import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import TablePagination from '@mui/material/TablePagination'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const SelectedRowsWrapper = styled.div`
	position: absolute;
	top: 5px;
	left: 35px;
`
interface IPaginationProps {
	rows: number
	selectedRows?: number
	itemsPerPage?: number
	filteredRows?: number
	currentPage: number
	setCurrentPage: (page: number) => void
	setItemsPerPage: (page: number) => void
	disabledRowPerPageSelection?: boolean
}

const Pagination = ({
	rows = 0,
	selectedRows = 0,
	currentPage,
	setCurrentPage,
	setItemsPerPage,
	itemsPerPage = 10,
	disabledRowPerPageSelection = false,
}: IPaginationProps) => {
	const { t } = useTranslation()
	const currentSlice = useMemo(() => {
		return getSlice(rows, itemsPerPage, currentPage)
	}, [currentPage, itemsPerPage, rows])

	const prevSlice = useRef(currentSlice)
	const [start, end] = currentSlice

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (disabledRowPerPageSelection) {
			return
		}
		setItemsPerPage(parseInt(event.target.value, 10))
	}

	useEffect(() => {
		const [prevStart, prevEnd] = prevSlice.current
		if (prevStart !== start || prevEnd !== end) {
			prevSlice.current = [start, end]
		}
	}, [start, end])

	return (
		<Box style={{ position: 'relative' }}>
			{selectedRows > 0 && (
				<SelectedRowsWrapper>
					<Typography
						variant="subtitle2"
						component="h5"
						color="textSecondary"
						style={{ marginTop: '10px' }}
					>
						{t('numSelected', { count: selectedRows })}
					</Typography>
				</SelectedRowsWrapper>
			)}
			<TablePagination
				component="div"
				count={rows}
				page={currentPage}
				rowsPerPage={itemsPerPage}
				rowsPerPageOptions={
					disabledRowPerPageSelection ? [5] : [10, 25, 50, 100]
				}
				onRowsPerPageChange={handleChangeRowsPerPage}
				onPageChange={(_, newPage) => setCurrentPage(newPage)}
				labelDisplayedRows={({ from, to, count }) => {
					return count === 0 ? 'No results' : `${from}-${to} of ${count}`
				}}
				SelectProps={{
					style: { minWidth: '50px' },
					inputProps: { style: { minWidth: '50px' } },
					SelectDisplayProps: {
						style: { border: 'none' },
					},
				}}
			/>
		</Box>
	)
}

const getSlice = (items: number, itemsPerPage: number, activePage: number) => {
	const endMax = items
	const startMax = Math.max(0, endMax - itemsPerPage - 1, items)

	const start = clamp(0, startMax, itemsPerPage * activePage)
	const end = clamp(0, endMax, start + itemsPerPage)

	return [start, end]
}

const clamp = (min: number, max: number, value: number) => {
	return Math.min(max, Math.max(value, min))
}

export { Pagination }
